import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, Divider, Button, Typography, Grid, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CustomConfirmationChangesDialog from "../../../containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeRef } from "../../../models/employees";
import { ExpenseReportDTO } from "../../../models/expense-reports";
import { ExpenseReportsApprovalFilter } from "../../../models/expense-reports/expense-reports-approval-filter";
import { PreApproveExpenseReportFilter } from "../../../models/pre-approve-expense-report/pre-approve-expense-report-filter";
import {
  approveExpenseReportsByLink,
  rejectExpenseReportsByLink,
} from "../../../services/expense-reports/expense-reports-api.service";
import { PreApproveExpenseReportApiService } from "../../../services/expense-reports/pre-approve-expense-reports-api.service";
import { EXPENSE_REPORT_STATUS } from "../../../utils/constants/constants";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { OVERRIDEN_ERROR_MESSAGES } from "../../../utils/toastify-response/overriden-error-messages";
import HeaderPortal from "../../my-t-and-a/HeaderPortal";
import ExpenseReportContent from "../../travel-and-expenses/ExpenseReport/AddEditExpenseReport/ExpenseReportContent/ExpenseReportContent";
import { ER_ALERT_BY_LINK_ID } from "../../travel-and-expenses/ExpenseReport/AddEditExpenseReport/ExpenseReportContent/utils";
import { ExpenseReportView } from "../../travel-and-expenses/ExpenseReport/utils";
import { SidebarLike } from "../SidebarLike/SidebarLike";
import {
  HeaderAndContentContainer,
  MainContainer,
  Container,
  ContentContainer,
  Content,
  HeaderContainer,
  TitleContainer,
  Title,
  StatusAmountContainer,
  StatusAmountBlock,
} from "../styles";
interface State {
  expenseReport: ExpenseReportDTO | null;
  error: string;
  loading: boolean;
  isDialogOpen: boolean;
  canOverrrideAudit: boolean;
  CCCDOpen: boolean;
  CCCDMessage: string;
}
const ManagerExpenseReportApprovalsByLink = ({ approveReject }: { approveReject: "approve" | "reject" }) => {
  const [searchParams] = useSearchParams();

  const expenseReportId = searchParams.get("er");
  const userId = searchParams.get("u");
  const theme = useTheme();
  const [state, setState] = useState<State>({
    expenseReport: null,
    error: "",
    loading: false,
    isDialogOpen: false,
    canOverrrideAudit: false,
    CCCDOpen: false,
    CCCDMessage: "",
  });

  enum DialogMessages {
    LOADING_APPROVE = "Approving expense report",
    LOADING_REJECT = "Rejecting expense report",
    APPROVED = "Expense report approved",
    REJECTED = "Expense report rejected",
    ALREADY_APPROVED = "Already approved",
    ALREADY_REJECTED = "Already rejected",
  }

  const preApproveExpenseReport = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new PreApproveExpenseReportApiService().loadItems({
        expenseReportId,
        userId,
      } as PreApproveExpenseReportFilter);
      if (!response.error) {
        setState((state) => ({
          ...state,
          canOverrrideAudit: response.items[0].canOverrideAudit,
          expenseReport: response.items[0].expenseReport,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
          canOverrideAudit: response.items[0].canOverrideAudit,
          expenseReport: response.items[0].expenseReport,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [expenseReportId, userId]);

  const approveExpenseReport = useCallback(async () => {
    if (expenseReportId) {
      setState((state) => ({ ...state, loading: true, isDialogOpen: true, CCCDOpen: false }));
      if (
        state.canOverrrideAudit ||
        (!state.canOverrrideAudit &&
          !state.expenseReport?.travelExpenses.find((travelExpense) => !travelExpense.auditAccepted))
      ) {
        try {
          const response =
            approveReject === "approve"
              ? await approveExpenseReportsByLink({
                  expenseReportIDs: [expenseReportId],
                  userId,
                } as ExpenseReportsApprovalFilter)
              : await rejectExpenseReportsByLink({
                  expenseReportIDs: [expenseReportId],
                  userId,
                } as ExpenseReportsApprovalFilter);

          if (!response.error) {
            setState((state) => ({ ...state, expenseReport: response.items[0] }));
          } else {
            setState((state) => ({ ...state, error: response.error }));
          }
        } catch (error) {
          setState((state) => ({ ...state, error: error as string }));
        } finally {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    }
  }, [approveReject, expenseReportId, userId]);

  useEffect(() => {
    // loadExpenseReport();
    preApproveExpenseReport();
  }, []);

  useEffect(() => {
    if (state.expenseReport) {
      const noTravelExpenses = state.expenseReport.travelExpenses.length === 0;

      const allTravelExpensesAccepted = !state.expenseReport.travelExpenses.find(
        (travelExpense) => !travelExpense.auditAccepted
      );
      const allTravelExpensesAudited = !state.expenseReport.travelExpenses.find(
        (travelExpense) => travelExpense.auditAccepted === null
      );

      const allTravelExpensesPending =
        state.expenseReport.travelExpenses.filter((travelExpense) => travelExpense.auditAccepted !== null).length === 0;

      const allTravelExpensesRejected =
        !noTravelExpenses &&
        !state.expenseReport.travelExpenses.find((travelExpense) => travelExpense.auditAccepted !== false);

      const notAllAudited =
        !allTravelExpensesAudited &&
        !allTravelExpensesPending &&
        state.expenseReport.travelExpenses.length > 1 &&
        state.expenseReport.travelExpenses.find((travelExpense) => travelExpense.auditAccepted === null) &&
        state.expenseReport.travelExpenses.find((travelExpense) => travelExpense.auditAccepted !== null);

      if (noTravelExpenses) {
        setState((state) => ({
          ...state,
          error: "Actions not allowed for expense report with no travel expenses.",
        }));
      } else if (state.expenseReport.status?.id === EXPENSE_REPORT_STATUS.submitted.id) {
        if (allTravelExpensesAccepted) {
          approveReject === "approve"
            ? approveExpenseReport()
            : setState((state) => ({
                ...state,
                CCCDOpen: true,
                CCCDMessage:
                  "All receipts are accepted by the auditor, are you sure you want to reject this expense report ?",
              }));
        } else if (allTravelExpensesRejected) {
          approveReject === "approve"
            ? setState((state) => ({
                ...state,
                error: "Accepting expense report is not allowed when all travel expense receipts are rejected.",
              }))
            : approveExpenseReport();
        } else if (allTravelExpensesAudited) {
          approveReject === "approve"
            ? setState((state) => ({
                ...state,
                CCCDOpen: true,
                CCCDMessage:
                  "Not all receipts are accepted by the auditor, are you sure you want to accept this expense report ?",
              }))
            : setState((state) => ({
                ...state,
                CCCDOpen: true,
                CCCDMessage:
                  "Some receipts are accepted by the auditor, are you sure you want to reject this expense report ?",
              }));
        } else if (notAllAudited) {
          state.canOverrrideAudit
            ? approveReject === "approve"
              ? setState((state) => ({
                  ...state,
                  CCCDOpen: true,
                  CCCDMessage:
                    "Not all receipts are reviewed by the auditor yet, are you sure you want to accept this expense report ? ",
                }))
              : setState((state) => ({
                  ...state,
                  CCCDOpen: true,
                  CCCDMessage:
                    "Not all receipts are reviewed by the auditor yet, are you sure you want to reject this expense report ? ",
                }))
            : approveReject === "approve"
            ? setState((state) => ({
                ...state,
                error: "Accepting expense report is not allowed until all receipts are accepted by the auditor",
              }))
            : setState((state) => ({
                ...state,
                error: "Rejecting expense report is not allowed until all receipts are reviewed by the auditor",
              }));
        } else if (allTravelExpensesPending) {
          state.canOverrrideAudit
            ? approveReject === "approve"
              ? setState((state) => ({
                  ...state,
                  CCCDOpen: true,
                  CCCDMessage:
                    "Receipts are not reviewed by the auditor yet, are you sure you want to accept this expense report ? ",
                }))
              : setState((state) => ({
                  ...state,
                  CCCDOpen: true,
                  CCCDMessage:
                    "Receipts are not reviewed by the auditor yet, are you sure you want to reject this expense report ? ",
                }))
            : approveReject === "approve"
            ? setState((state) => ({
                ...state,
                error: "Accepting expense report is not allowed until all receipts are accepted by the auditor",
              }))
            : setState((state) => ({
                ...state,
                error: "Rejecting expense report is not allowed until all receipts are reviewed by the auditor",
              }));
        }
      } else {
        setState((state) => ({
          ...state,
          error:
            state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.cancelled.id
              ? `Expense report cancelled by ${state.expenseReport.modifiedByUser?.name ?? "<missing user name>"} on ${
                  state.expenseReport?.modifiedAt
                    ? `${intlFormatDateShort(new Date(state.expenseReport?.modifiedAt))}`
                    : "<missing modification date>"
                }`
              : state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id
              ? `Expense report returned to employee by ${
                  state.expenseReport.modifiedByUser?.name ?? "<missing user name>"
                } on ${
                  state.expenseReport?.modifiedAt
                    ? `${intlFormatDateShort(new Date(state.expenseReport?.modifiedAt))}`
                    : "<missing modification date>"
                }`
              : state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.paid.id
              ? `Expense report paid by ${state.expenseReport.modifiedByUser?.name ?? "<missing user name>"} on ${
                  state.expenseReport?.modifiedAt
                    ? `${intlFormatDateShort(new Date(state.expenseReport?.modifiedAt))}`
                    : "<missing modification date>"
                }`
              : state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.rejected.id
              ? `Expense report rejected by ${
                  state.expenseReport.approvedRejectedByUser?.name ?? "<missing user name>"
                } on ${
                  state.expenseReport?.approvedRejectedAt
                    ? `${intlFormatDateShort(new Date(state.expenseReport?.approvedRejectedAt))}`
                    : "<missing modification date>"
                }`
              : state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.approved.id
              ? `Expense report approved by ${
                  state.expenseReport.approvedRejectedByUser?.name ?? "<missing user name>"
                } on ${
                  state.expenseReport?.approvedRejectedAt
                    ? `${intlFormatDateShort(new Date(state.expenseReport?.approvedRejectedAt))}`
                    : "<missing modification date>"
                }`
              : "",
        }));
      }
    }
  }, [state.canOverrrideAudit, state.expenseReport, approveExpenseReport]);

  return (
    <>
      <LoadingOverlay loading={state.loading}>
        <MainContainer>
          <Container>
            <SidebarLike />
            <HeaderAndContentContainer>
              <HeaderContainer>
                <TitleContainer>
                  <Title variant="h5">{`${approveReject === "approve" ? "Approve" : "Reject"} expense report`}</Title>
                  <Divider orientation="vertical" />
                  <Typography>{state.expenseReport?.employee?.name}</Typography>
                </TitleContainer>

                <HeaderPortal
                  title={`${approveReject === "approve" ? "Approve" : "Reject"} expense report`}
                  employee={<Typography>{state.expenseReport?.employee?.name}</Typography>}
                />
              </HeaderContainer>

              <ContentContainer>
                <Content sx={{ height: "100%" }}>
                  {state.error && (
                    <Alert
                      id={ER_ALERT_BY_LINK_ID}
                      style={{ marginBottom: "10px" }}
                      severity={`${OVERRIDEN_ERROR_MESSAGES.includes(state.error) ? "info" : "error"}`}
                    >
                      {`${
                        OVERRIDEN_ERROR_MESSAGES.includes(state.error)
                          ? `${state.error} by ${state.expenseReport?.approvedRejectedByUser?.name ?? ""} on ${
                              state.expenseReport?.approvedRejectedAt
                                ? `${intlFormatDateShort(new Date(state.expenseReport?.approvedRejectedAt))}`
                                : ""
                            }`
                          : `${state.error}`
                      }`}
                    </Alert>
                  )}
                  {state.expenseReport && (
                    <>
                      <StatusAmountContainer>
                        <StatusAmountBlock>
                          <Typography> Status : </Typography>
                          <Typography> {state.expenseReport?.status?.name} </Typography>
                          {state.expenseReport?.documentation &&
                          (state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.submitted.id ||
                            state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id) ? (
                            <Typography variant="body2">(Documentation audit marked as OK)</Typography>
                          ) : (
                            ""
                          )}
                        </StatusAmountBlock>
                        <StatusAmountBlock>
                          <Typography sx={{ fontWeight: "bold" }}>Total :</Typography>
                          <Typography>{state.expenseReport?.totalAmount?.toLocaleCustom()}</Typography>
                          <Typography>{state.expenseReport?.currency?.code ?? ""}</Typography>
                        </StatusAmountBlock>
                      </StatusAmountContainer>
                      <ExpenseReportContent
                        employee={state.expenseReport?.employee as EmployeeRef}
                        expenseReport={state.expenseReport}
                        view={ExpenseReportView.employee}
                        wholeReportIsReadOnly
                        hideAuditNotes
                        isManagedByLink
                      />
                    </>
                  )}
                  <Dialog open={state.isDialogOpen}>
                    <DialogContent>
                      <Grid
                        style={{
                          padding: "10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "30px",
                        }}
                      >
                        {state.loading ? (
                          <LoadingOverlay loading={state.loading}>
                            <Typography variant="h5">
                              {approveReject === "approve"
                                ? DialogMessages.LOADING_APPROVE
                                : DialogMessages.LOADING_REJECT}
                            </Typography>
                          </LoadingOverlay>
                        ) : state.expenseReport && !state.error ? (
                          approveReject === "approve" ? (
                            <Typography variant="h5">
                              {DialogMessages.APPROVED}
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                color={theme.palette.success.main}
                              />
                            </Typography>
                          ) : (
                            <Typography variant="h5">
                              {DialogMessages.REJECTED}
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                color={theme.palette.success.main}
                              />
                            </Typography>
                          )
                        ) : state.error ? (
                          <Alert severity={`${OVERRIDEN_ERROR_MESSAGES.includes(state.error) ? "info" : "error"}`}>
                            {`${
                              OVERRIDEN_ERROR_MESSAGES.includes(state.error)
                                ? `${state.error} by ${state.expenseReport?.approvedRejectedByUser?.name ?? ""} on ${
                                    state.expenseReport?.approvedRejectedAt
                                      ? `${intlFormatDateShort(new Date(state.expenseReport?.approvedRejectedAt))}`
                                      : ""
                                  }`
                                : `${state.error}`
                            }`}
                          </Alert>
                        ) : (
                          <Typography variant="h5">
                            {approveReject === "approve"
                              ? DialogMessages.ALREADY_APPROVED
                              : DialogMessages.ALREADY_REJECTED}
                          </Typography>
                        )}

                        <Button
                          variant={"contained"}
                          onClick={() => setState((state) => ({ ...state, isDialogOpen: false }))}
                          style={{ width: "fit-content", alignSelf: "center" }}
                          disabled={state.loading}
                        >
                          Okay
                        </Button>
                      </Grid>
                    </DialogContent>
                  </Dialog>

                  <CustomConfirmationChangesDialog
                    open={state.CCCDOpen}
                    loading={false}
                    actionsAndHandlers={[
                      {
                        action: `Yes , ${approveReject === "approve" ? "accept" : "reject"}`,
                        handler: approveExpenseReport,
                        buttonVariant: "contained",
                      },
                    ]}
                    title={`Confirm ${
                      approveReject === "approve" ? "accepting" : "rejecting"
                    } non audited expense report`}
                    handleCancel={() => setState((state) => ({ ...state, CCCDOpen: false }))}
                  >
                    <Alert severity="warning">{state.CCCDMessage}</Alert>
                  </CustomConfirmationChangesDialog>
                </Content>
              </ContentContainer>
            </HeaderAndContentContainer>
          </Container>
        </MainContainer>
      </LoadingOverlay>
    </>
  );
};
export default ManagerExpenseReportApprovalsByLink;
